import {
  Button,
  Chip,
  ErrorBanner,
  InfoChip,
  Kbd,
  MarkupText,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  plainMarkup,
  QueueRow,
  Skeleton,
  Tooltip,
  useIsSinglePane,
} from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import {
  AlarmClock,
  Check,
  ChevronsUpDown,
  Clock3,
  Eye,
  Link,
  ListTodo,
  MailOpen,
  MessageSquareMore,
  MoreHorizontal,
  Phone,
  SquareCheckBig,
  Truck,
  UserRoundCheck,
} from 'lucide-react'
import * as React from 'react'
import { TicketBadge } from '../../components/ticket-ref'
import type { CaseCloseoutOut, CaseRow } from '../../lib/api/generated'
import { contactLabel } from '../../lib/contact'
import { formatLaneBasis, formatWhen } from '../../lib/display-time'
import { flagLabel } from '../../lib/flags'
import { unreadOf } from '../../lib/unread'
import { BrandChip } from '../brands/chip'
import { useBrandScope } from '../brands/use-brand-scope'
import { BlockerSummaryChip } from '../closeout'
import { renderCaseParty } from '../directory/party-link'
import { QueueLiveChip } from '../live-work'
import { QueueProcurementChip } from '../procurement'
import { SlaChip } from '../sla/sla-chip'
import { TechReportMenuItems } from '../visit-outcome/copy-tech-link'
import { queueErrorText } from './api'
import { OverdueActions, type OverdueParty } from './overdue-actions'
import { queueExpansionQuery } from './queries'

function dueOf(row: CaseRow): { label: string; overdue?: boolean } | undefined {
  const due = row.next_move?.due_at
  if (!due) return undefined
  const date = new Date(due)
  if (Number.isNaN(date.getTime())) return { label: 'Due time unreadable', overdue: row.next_move?.overdue }
  const formatted = formatWhen(date.toISOString(), { style: 'absolute' })
  return {
    // The row summary carries the move itself. Keep the urgent chip short enough to survive a narrow desktop pane;
    // the full due time remains in the non-overdue label and in the case's next-move strip.
    label: row.next_move?.overdue ? 'Next move overdue' : `Next move due · ${formatted}`,
    overdue: row.next_move?.overdue,
  }
}

/** Compact age for the one-line overdue-waiting chip (#715). */
export function compactOverdueAge(value: string | null | undefined, now = Date.now()): string | null {
  if (!value) return null
  const parsed = Date.parse(value)
  if (Number.isNaN(parsed)) return null
  const minutes = Math.max(0, now - parsed) / 60_000
  if (minutes < 60) return `${Math.max(1, Math.round(minutes))}m`
  const hours = minutes / 60
  if (hours < 48) return `${Math.max(1, Math.round(hours))}h`
  return `${Math.max(1, Math.round(hours / 24))}d`
}

export function overdueWaitingChip(row: CaseRow, now = Date.now()): { label: string; tooltip: string } | null {
  const party = row.overdue_waiting_party
  if (!party) return null
  const timestamp = typeof row.lane_basis === 'object' ? row.lane_basis?.timestamp : row.next_move?.due_at
  const age = compactOverdueAge(timestamp ?? row.next_move?.due_at, now)
  const basis = formatLaneBasis(row.lane_basis, now)
  return {
    label: `Overdue · ${party}${age ? ` ${age}` : ''}`,
    tooltip: basis || `${party[0]?.toUpperCase()}${party.slice(1)} has not completed the next move.`,
  }
}

const CALL_OUTCOME: Record<string, string> = {
  resolved: 'resolved',
  transferred: 'transferred',
  callback_booked: 'callback booked',
  voicemail: 'voicemail',
  caller_hung_up: 'caller hung up',
  failed: 'failed',
}

/**
 * Who the row is for: the resolved account, else the Desk contact ("Name <email>" → "Name · email") marked as not
 * resolved. Never a stand-in like "Customer name unavailable" when a contact is known.
 */
export function rowWho(row: Pick<CaseRow, 'customer_display' | 'contact_display' | 'identity_state'>): {
  text: string
  unresolved: boolean
} {
  if (row.customer_display?.trim()) return { text: row.customer_display, unresolved: false }
  const contact = row.contact_display?.trim()
  if (contact) return { text: contactLabel(contact), unresolved: true }
  return { text: 'Customer not identified yet', unresolved: true }
}

/** Older API responses may omit the greeting-safe fallback; absence is not "unreachable". */
export { contactLabel }

export const NO_SUMMARY = 'No summary yet: Riley builds it on the next pass'

export function ExpandedCase({ row }: { row: CaseRow }) {
  const expansion = useQuery(queueExpansionQuery(row.id, true))
  if (expansion.isPending) {
    return (
      <div role="status" aria-label="Loading last turn and draft" className="space-y-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }
  if (expansion.isError) {
    return (
      <ErrorBanner
        title="Last turn and draft unreachable"
        detail={queueErrorText(expansion.error)}
        onRetry={() => void expansion.refetch()}
      />
    )
  }
  const { lastTurn, draft, asOf } = expansion.data
  return (
    <div className="space-y-3">
      {asOf ? <Chip tone="warning">Live Desk unreachable · mirror as of {formatWhen(asOf, { style: 'absolute' })}</Chip> : null}
      <section aria-label="Last turn">
        <p className="mb-1 text-xs font-medium text-subtle">Last turn</p>
        {lastTurn ? (
          <div className="rounded-md bg-muted px-3 py-2 text-sm">
            <p className="mb-1 text-xs text-muted-foreground">
              {lastTurn.author_ref || lastTurn.author_kind} ·{' '}
              {lastTurn.sent_at ? formatWhen(lastTurn.sent_at, { style: 'absolute' }) : 'time unknown'}
            </p>
            <p className="whitespace-pre-wrap">
              {lastTurn.text_clean ? <MarkupText text={lastTurn.text_clean} mentions={lastTurn.mentions} /> : 'Turn content unreachable.'}
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No customer turn matched in the mirror.</p>
        )}
      </section>
      {draft ? (
        <section
          aria-label="Unsent draft"
          className="rounded-md border border-dashed border-primary/40 bg-primary-soft/40 px-3 py-2 text-sm"
        >
          <p className="mb-1 text-xs font-semibold text-primary">[DRAFT - not sent]</p>
          <p className="whitespace-pre-wrap">
            {draft.text_clean ? <MarkupText text={draft.text_clean} mentions={draft.mentions} /> : 'Draft content unreachable.'}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">This draft does not settle the turn or move the case from Reply owed.</p>
        </section>
      ) : row.draft_ready ? (
        <p className="text-sm text-muted-foreground">Draft marked ready, but its body is unreachable.</p>
      ) : null}
    </div>
  )
}

export interface RowActions {
  /** Required by queue surfaces; optional only for isolated fixture renderers. */
  peek?: () => void
  select(): void
  take(): void
  nextMove(): void
  snooze(): void
  /** #746 1.8: set on an overdue wait; the one OverdueActions control draws Chase / Re-plan / Snooze from it. */
  overdueParty?: OverdueParty
  /** #694: open the overdue owner's existing audience pane and composer (a technician gets the update card). */
  chase?: () => void
  link(): void
  dispatch(): void
  time(): void
  expand(): void
  /** Overdue by technician only: propose the `p` nudge to the move's technician. */
  nudge?: () => void
  /** #876: Mark unread (row is read) or Mark read (row is unread), this person only. */
  toggleRead?: () => void
  /** #788: Reply owed rows only. The newest message needs no reply. */
  noReply?: () => void
  /** #928: correct the newest customer turn's reply decision without opening the case. */
  needsReply?: () => void
}

/** The row's left-edge selection target. It peeks without triggering the row's open action. */
export function QueuePeekGutter({ label, selected, onPeek }: { label: string; selected: boolean; onPeek: () => void }) {
  return (
    <button
      type="button"
      aria-label={`Peek ${label}`}
      aria-pressed={selected}
      title="Peek without opening (P)"
      onClick={(event) => {
        event.stopPropagation()
        onPeek()
      }}
      className="absolute top-0 left-0 z-10 flex h-12 w-6 cursor-pointer items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <span
        aria-hidden
        className={`size-2.5 rounded-sm border transition-colors ${selected ? 'border-primary bg-primary shadow-sm' : 'border-border-strong bg-card group-hover:border-primary/70'}`}
      />
    </button>
  )
}

/**
 * The left dot of a queue row as a multi-select checkbox (#721). It is a quiet dot until the row is hovered or
 * focused, shows a box once anything is checked, and a click toggles; shift-click selects the range.
 */
export function QueueSelectGutter({
  label,
  checked,
  selectMode,
  onToggle,
}: {
  label: string
  checked: boolean
  selectMode: boolean
  onToggle: (shift: boolean) => void
}) {
  const boxed = checked || selectMode
  return (
    <div
      title="Select (X) · Shift-click for a range"
      className="absolute top-0 left-0 z-10 flex h-12 w-7 items-center justify-center has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary"
    >
      <input
        type="checkbox"
        className="absolute inset-0 size-full cursor-pointer opacity-0"
        checked={checked}
        aria-label={`Select ${label}`}
        onChange={() => undefined}
        onClick={(event) => {
          event.stopPropagation()
          onToggle(event.shiftKey)
        }}
      />
      <span
        aria-hidden
        className={`pointer-events-none flex items-center justify-center border transition-all ${
          checked
            ? 'size-4 rounded border-primary bg-primary text-primary-foreground'
            : boxed
              ? 'size-4 rounded border-border-strong bg-card'
              : 'size-2.5 rounded-sm border-border-strong bg-card group-hover:size-4 group-hover:rounded group-hover:border-primary group-focus-within:size-4 group-focus-within:border-primary'
        }`}
      >
        {checked ? <Check className="size-3" strokeWidth={3} /> : null}
      </span>
    </div>
  )
}

/** Visible in every row action bar: select this row for the cached rail without opening the case. */
export function QueuePeekAction({ selected, onPeek }: { selected: boolean; onPeek: () => void }) {
  return (
    <Button
      size="sm"
      variant={selected ? 'primary' : 'ghost'}
      aria-label="Peek row"
      title="Peek · P"
      onClick={(event) => {
        event.stopPropagation()
        onPeek()
      }}
      className="gap-1 px-2"
    >
      <Eye /> <span>Peek</span> <span aria-hidden>·</span> <Kbd keys="p" />
    </Button>
  )
}

function HoverActions({
  selected,
  checked,
  unread,
  actions,
  techReportCaseId = null,
}: {
  selected: boolean
  checked: boolean
  unread: boolean
  actions: RowActions
  /** Set on a Waiting-on-tech-report row so Copy tech link loads only when this menu opens. */
  techReportCaseId?: string | null
}) {
  const [menuOpen, setMenuOpen] = React.useState(false)
  const click = (action: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation()
    action()
  }
  return (
    <div
      role="toolbar"
      aria-label="Row actions"
      className="pointer-events-none flex items-center gap-0.5 rounded-md border border-border bg-card/95 p-0.5 opacity-0 shadow-sm transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100"
    >
      <div data-testid="queue-row-primary-actions" className="contents @max-[36rem]:hidden">
        {actions.peek ? <QueuePeekAction selected={selected} onPeek={actions.peek} /> : null}
        <Button
          size="icon-sm"
          variant={checked ? 'primary' : 'ghost'}
          aria-label={checked ? 'Remove from bulk selection' : 'Add to bulk selection'}
          title="Multi-select (x)"
          onClick={click(actions.select)}
        >
          <SquareCheckBig />
        </Button>
        <Button size="icon-sm" variant="ghost" aria-label="Expand row" title="Last turn + draft (Space)" onClick={click(actions.expand)}>
          <ChevronsUpDown />
        </Button>
        <Button size="icon-sm" variant="ghost" aria-label="Take or assign" title="Take / assign (a)" onClick={click(actions.take)}>
          <UserRoundCheck />
        </Button>
        {actions.overdueParty ? (
          <OverdueActions
            party={actions.overdueParty}
            variant="icons"
            onChase={actions.chase}
            onReplan={actions.nextMove}
            onSnooze={actions.snooze}
            onClick={click}
          />
        ) : (
          <Button size="icon-sm" variant="ghost" aria-label="Set next move" title="Set next move (n)" onClick={click(actions.nextMove)}>
            <ListTodo />
          </Button>
        )}
      </div>
      <Menu onOpenChange={setMenuOpen}>
        <MenuTrigger
          aria-label="More row actions"
          title="More actions"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          className="inline-flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
        >
          <MoreHorizontal className="size-4" />
        </MenuTrigger>
        <MenuContent
          // Base UI portals this popup outside the row's DOM. Stop its clicks here so QueueRow cannot mistake a
          // menu action for a row click and open the case after the requested action runs.
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
        >
          {actions.peek ? (
            <MenuItem onClick={actions.peek} keys="p">
              <Eye /> Peek
            </MenuItem>
          ) : null}
          <MenuItem onClick={actions.select} keys="x">
            <SquareCheckBig /> {checked ? 'Remove from selection' : 'Add to selection'}
          </MenuItem>
          <MenuItem onClick={actions.expand} keys="space">
            <ChevronsUpDown /> Last turn + draft
          </MenuItem>
          <MenuItem onClick={actions.take} keys="a">
            <UserRoundCheck /> Take or assign
          </MenuItem>
          {actions.toggleRead ? (
            <MenuItem onClick={actions.toggleRead}>
              <MailOpen /> {unread ? 'Mark read' : 'Mark unread'}
            </MenuItem>
          ) : null}
          <MenuSeparator />
          {actions.nudge ? (
            <MenuItem onClick={actions.nudge} keys="shift+p">
              <MessageSquareMore /> Ask tech for update
            </MenuItem>
          ) : null}
          {actions.overdueParty ? (
            <OverdueActions
              party={actions.overdueParty}
              variant="menu"
              onChase={actions.chase}
              onReplan={actions.nextMove}
              onSnooze={actions.snooze}
            />
          ) : (
            <>
              <MenuItem onClick={actions.nextMove} keys="n">
                <ListTodo /> Set next move
              </MenuItem>
              <MenuItem onClick={actions.snooze} keys="s">
                <AlarmClock /> Snooze
              </MenuItem>
            </>
          )}
          <MenuItem onClick={actions.link} keys="l">
            <Link /> Link a ticket to this case
          </MenuItem>
          <MenuItem onClick={actions.dispatch} keys="d">
            <Truck /> Dispatch
          </MenuItem>
          <MenuItem onClick={actions.time} keys="t">
            <Clock3 /> Log time
          </MenuItem>
          {techReportCaseId ? (
            <>
              <MenuSeparator />
              <TechReportMenuItems caseId={techReportCaseId} active={menuOpen} />
            </>
          ) : null}
        </MenuContent>
      </Menu>
    </div>
  )
}

function chipSeverity(chip: HTMLElement): number {
  const classes = chip.classList
  if (classes.contains('text-destructive')) return 0
  if (classes.contains('text-warning')) return 1
  if (
    classes.contains('text-primary') ||
    classes.contains('text-riley') ||
    classes.contains('text-audience-customer') ||
    classes.contains('text-audience-contractor') ||
    classes.contains('text-audience-internal')
  )
    return 2
  if (classes.contains('text-success')) return 3
  if (classes.contains('text-muted-foreground')) return 4
  return 5
}

interface ChipSnapshot {
  visible: number[]
  hidden: string[]
}

/**
 * Keep the row on one line: rank chips by severity, then fit as many as the allocated width allows. Async chips can
 * appear later and pane resizing changes the budget, so both the contents and the strip itself are observed.
 */
export function QueueChipOverflow({ children, max = Number.POSITIVE_INFINITY }: { children: React.ReactNode; max?: number }) {
  const items = React.Children.toArray(children)
  const itemCount = items.length
  const stripRef = React.useRef<HTMLSpanElement>(null)
  const moreRef = React.useRef<HTMLSpanElement>(null)
  const refs = React.useRef(new Map<number, HTMLSpanElement>())
  const [snapshot, setSnapshot] = React.useState<ChipSnapshot>({ visible: items.map((_item, index) => index), hidden: [] })

  React.useLayoutEffect(() => {
    const inspect = () => {
      const strip = stripRef.current
      if (!strip) return
      const available = strip.clientWidth
      const wrappers = [...refs.current.entries()].filter(([index]) => index < itemCount)
      const styles = wrappers.map(([, wrapper]) => wrapper.style.cssText)
      for (const [, wrapper] of wrappers) {
        // Hidden chips still need an intrinsic width for the next fit. This synchronous measurement is restored
        // before paint, so hidden controls never become visible or focusable.
        wrapper.style.cssText = 'display:inline-flex;position:absolute;visibility:hidden;width:max-content;pointer-events:none;'
      }
      const ranked = [...refs.current.entries()]
        .filter(([index]) => index < itemCount)
        .map(([index, wrapper]) => {
          const chip = wrapper.querySelector<HTMLElement>('[data-slot="chip"]')
          return chip
            ? {
                index,
                rank: chipSeverity(chip),
                label: chip.textContent?.trim() || chip.getAttribute('aria-label') || 'Status',
                width: Math.ceil(wrapper.getBoundingClientRect().width),
              }
            : null
        })
        .filter((item): item is NonNullable<typeof item> => item !== null)
        .sort((left, right) => left.rank - right.rank || left.index - right.index)
      wrappers.forEach(([, wrapper], index) => {
        wrapper.style.cssText = styles[index] ?? ''
      })

      const gap = 6
      const moreWidth = Math.ceil(moreRef.current?.getBoundingClientRect().width ?? 28)
      const cap = Math.min(ranked.length, max)
      const selected: (typeof ranked)[number][] = []
      // DOM-only tests and a strip hidden during its first layout pass can report no width. Keep an explicit cap
      // deterministic in that state; the ResizeObserver replaces this fallback with the measured fit once shown.
      if (available <= 0) selected.push(...ranked.slice(0, cap))
      let used = 0
      if (available > 0) {
        for (const item of ranked) {
          if (selected.length >= cap) break
          const nextUsed = used + (selected.length > 0 ? gap : 0) + item.width
          const hiddenAfter = ranked.length - selected.length - 1
          const reserveOverflow = hiddenAfter > 0 ? gap + moreWidth : 0
          if (nextUsed + reserveOverflow > available) break
          selected.push(item)
          used = nextUsed
        }
      }
      const selectedIds = new Set(selected.map((item) => item.index))
      const next = {
        visible: selected.map((item) => item.index),
        hidden: ranked.filter((item) => !selectedIds.has(item.index)).map((item) => item.label),
      }
      setSnapshot((current) =>
        current.visible.join(',') === next.visible.join(',') && current.hidden.join('\n') === next.hidden.join('\n') ? current : next,
      )
    }
    const observer = new MutationObserver(inspect)
    for (const wrapper of refs.current.values()) observer.observe(wrapper, { childList: true, subtree: true, characterData: true })
    const resizeObserver = globalThis.ResizeObserver ? new ResizeObserver(inspect) : null
    if (stripRef.current) resizeObserver?.observe(stripRef.current)
    inspect()
    return () => {
      observer.disconnect()
      resizeObserver?.disconnect()
    }
  }, [itemCount, max])

  const visible = new Set(snapshot.visible)
  return (
    <span
      ref={stripRef}
      className="relative flex min-w-0 flex-1 flex-nowrap items-center gap-1.5 overflow-hidden"
      data-testid="queue-chip-strip"
    >
      {items.map((item, index) => (
        <span
          key={(item as React.ReactElement<{ key?: React.Key }>).key ?? index}
          ref={(node) => {
            if (node) refs.current.set(index, node)
            else refs.current.delete(index)
          }}
          className={visible.has(index) ? 'inline-flex shrink-0' : 'hidden'}
          style={visible.has(index) ? { order: snapshot.visible.indexOf(index) } : undefined}
          data-queue-chip-visible={visible.has(index) ? 'true' : 'false'}
        >
          {item}
        </span>
      ))}
      <span ref={moreRef} aria-hidden className="pointer-events-none invisible absolute inline-flex w-max">
        <Chip tone="neutral">+{Math.max(1, itemCount)}</Chip>
      </span>
      {snapshot.hidden.length > 0 ? (
        <Tooltip
          content={
            <span className="block max-w-80 whitespace-normal" data-testid="queue-chip-overflow-list">
              {snapshot.hidden.map((label) => (
                <span key={label} className="block">
                  {label}
                </span>
              ))}
            </span>
          }
        >
          <Chip
            tone="neutral"
            className="order-[99]"
            aria-label={`${snapshot.hidden.length} more statuses`}
            data-testid="queue-chip-overflow"
          >
            +{snapshot.hidden.length}
          </Chip>
        </Tooltip>
      ) : null}
    </span>
  )
}

/** #271: why a row is in Overdue by technician when it is not an overdue next move (that one shows on the due chip). */
export const OVERDUE_REASON: Record<NonNullable<CaseRow['overdue_reasons']>[number], string> = {
  next_move_overdue: 'Next move overdue',
  sla_response_overdue: 'SLA response overdue',
  sla_resolution_overdue: 'SLA resolution overdue',
}

/**
 * Phone: a long touch on a row enters select mode by toggling it. The click that follows the lift is swallowed so
 * the row does not also open.
 */
function useTouchLongPress(onLongPress: () => void, enabled: boolean) {
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const fired = React.useRef(false)
  const cancel = React.useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
  }, [])
  React.useEffect(() => cancel, [cancel])
  if (!enabled) return { handlers: {} }
  return {
    handlers: {
      onPointerDown: (event: React.PointerEvent) => {
        if (event.pointerType !== 'touch') return
        fired.current = false
        cancel()
        timer.current = setTimeout(() => {
          fired.current = true
          onLongPress()
        }, 500)
      },
      onPointerUp: cancel,
      onPointerMove: cancel,
      onPointerCancel: cancel,
      onClickCapture: (event: React.MouseEvent) => {
        if (!fired.current) return
        fired.current = false
        event.stopPropagation()
        event.preventDefault()
      },
    },
  }
}

export function QueueCaseRow({
  row,
  selected,
  checked,
  selectMode = false,
  onToggleChecked,
  expanded,
  onSelect,
  onOpen,
  onOpenActions,
  actions,
  closeout,
}: {
  row: CaseRow
  /** This row's entry in the batched close-out read (#121); undefined while loading or unreadable. */
  closeout?: CaseCloseoutOut
  selected: boolean
  checked: boolean
  /** Something in the queue is checked: every row shows its checkbox. */
  selectMode?: boolean
  /** Click on the left checkbox (#721); shift extends a range. Without it the gutter only peeks. */
  onToggleChecked?: (shift: boolean) => void
  expanded: boolean
  onSelect: () => void
  onOpen: () => void
  /** Phone: opens the labeled action sheet for this row instead of the hover toolbar. */
  onOpenActions?: () => void
  actions: RowActions
}) {
  const singlePane = useIsSinglePane()
  const brandScope = useBrandScope()
  const brandId = row.brand_id
  const brand =
    brandId === undefined ? undefined : brandId === null ? null : (brandScope.brands.find((entry) => entry.id === brandId) ?? null)
  const longPress = useTouchLongPress(() => onToggleChecked?.(false), Boolean(onToggleChecked))
  const due = dueOf(row)
  const laneBasis = formatLaneBasis(row.lane_basis)
  const overdueChip = overdueWaitingChip(row)
  const chips = (
    <QueueChipOverflow>
      {brandScope.isAll && brandScope.showBrands ? <BrandChip brand={brand} /> : null}
      {overdueChip ? (
        // #1232: the basis is a tap/Enter away on a phone or keyboard, not only on hover.
        <InfoChip tone="danger" info={overdueChip.tooltip} data-testid="overdue-waiting-chip">
          {overdueChip.label}
        </InfoChip>
      ) : due ? (
        <Chip tone={due.overdue ? 'danger' : 'neutral'}>{due.label}</Chip>
      ) : null}
      <SlaChip sla={row.sla} />
      {(row.overdue_reasons ?? [])
        .filter((r) => r !== 'next_move_overdue')
        .map((r) => (
          <Chip key={r} tone="danger" data-testid="overdue-reason">
            {OVERDUE_REASON[r]}
          </Chip>
        ))}
      {row.spam_rescued ? <Chip tone="warning">unspammed</Chip> : null}
      {row.callback ? (
        row.callback.call_href ? (
          <a
            href={row.callback.call_href}
            aria-label={`Callback owed${row.callback.caller_number ? `: ${row.callback.caller_number}` : ''}`}
            onClick={(event) => event.stopPropagation()}
          >
            <Chip tone="primary">
              <Phone className="size-3.5" /> Callback owed
            </Chip>
          </a>
        ) : (
          <Chip tone="primary">
            <Phone className="size-3.5" /> Callback owed
          </Chip>
        )
      ) : null}
      {row.riley_call ? (
        <Chip tone={row.riley_call.state === 'live' ? 'primary' : row.riley_call.outcome === 'failed' ? 'warning' : 'neutral'}>
          <Phone className="size-3.5" /> Riley call ·{' '}
          {row.riley_call.state === 'live' ? 'live' : CALL_OUTCOME[row.riley_call.outcome ?? ''] || 'ended'}
        </Chip>
      ) : null}
      {row.draft_ready && row.conversation_state === 'reply_owed' ? <Chip tone="primary">Draft ready</Chip> : null}
      {row.latest_customer_turn_id && row.reply_classification === 'needs_reply' && actions.noReply ? (
        <Chip
          tone="outline"
          role="button"
          tabIndex={0}
          title="Nothing to answer: clear Reply owed (undoable)"
          data-testid="no-reply-needed-chip"
          className="cursor-pointer hover:bg-muted"
          onClick={(event) => {
            event.stopPropagation()
            actions.noReply?.()
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return
            event.preventDefault()
            event.stopPropagation()
            actions.noReply?.()
          }}
        >
          No reply needed
        </Chip>
      ) : null}
      {row.latest_customer_turn_id && row.reply_classification !== 'needs_reply' && actions.needsReply ? (
        <Chip
          tone="outline"
          role="button"
          tabIndex={0}
          title="This customer message needs an answer: move it to Reply owed"
          data-testid="needs-reply-chip"
          className="cursor-pointer hover:bg-muted"
          onClick={(event) => {
            event.stopPropagation()
            actions.needsReply?.()
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return
            event.preventDefault()
            event.stopPropagation()
            actions.needsReply?.()
          }}
        >
          Needs a reply
        </Chip>
      ) : null}
      <QueueLiveChip caseId={row.id} />
      <QueueProcurementChip row={row} />
      {closeout ? <BlockerSummaryChip data={closeout} /> : null}
      {row.flags?.map((flag) => (
        <Chip key={flag} tone="warning" title={flag}>
          {flagLabel(flag)}
        </Chip>
      ))}
      {checked ? <Chip tone="primary">Selected</Chip> : null}
      {row.draft_ready && row.conversation_state === 'reply_owed' ? (
        <span className="sr-only">Draft ready; reply is still owed.</span>
      ) : null}
    </QueueChipOverflow>
  )

  const who = rowWho(row)
  return (
    <div
      className={`@container group relative [&>[role=option]]:pl-7 ${
        singlePane
          ? '[&_[data-testid=queue-row-case-title]]:overflow-visible [&_[data-testid=queue-row-case-title]]:text-clip [&_[data-testid=queue-row-case-title]]:whitespace-normal [&_[data-testid=queue-row-case-title]]:break-words [&_[data-testid=queue-row-customer]]:overflow-visible [&_[data-testid=queue-row-customer]]:text-clip [&_[data-testid=queue-row-customer]]:whitespace-normal [&_[data-testid=queue-row-customer]]:break-words'
          : ''
      }`}
      title={laneBasis ? `In this lane because: ${laneBasis}` : undefined}
      {...longPress.handlers}
    >
      {onToggleChecked ? (
        <QueueSelectGutter label={row.number} checked={checked} selectMode={selectMode} onToggle={onToggleChecked} />
      ) : (
        <QueuePeekGutter label={row.number} selected={selected} onPeek={onSelect} />
      )}
      <QueueRow
        id={row.id}
        customer={who.text}
        renderCustomer={renderCaseParty(row)}
        customerHint={who.unresolved && row.contact_display ? 'account not resolved' : undefined}
        title={`${row.number} · ${row.title}`}
        tickets={row.tickets.filter((ticket) => !ticket.tombstoned).map((ticket) => ticket.ticket_number)}
        renderTicket={(n) => (
          <TicketBadge
            number={n}
            caseNumber={row.number}
            deskTicketId={row.tickets.find((ticket) => ticket.ticket_number === String(n))?.desk_ticket_id}
          />
        )}
        owner={row.owner ? { name: row.owner.name } : undefined}
        unread={unreadOf(row)}
        summary={plainMarkup(row.summary_line) || NO_SUMMARY}
        nextMove={row.next_move?.text || 'set a next move'}
        chips={chips}
        selected={selected}
        expanded={expanded}
        expandedContent={expanded ? <ExpandedCase row={row} /> : undefined}
        actions={
          singlePane ? undefined : (
            <HoverActions
              selected={selected}
              checked={checked}
              unread={Boolean(row.unread)}
              actions={actions}
              techReportCaseId={row.lane === 'tech_report' ? row.id : null}
            />
          )
        }
        footer={
          singlePane ? (
            <Button
              size="sm"
              variant="outline"
              aria-label="Row actions"
              className="min-h-11"
              onClick={(event) => {
                event.stopPropagation()
                onOpenActions?.()
              }}
            >
              <MoreHorizontal /> Actions
            </Button>
          ) : null
        }
        // A click opens the case (Enter too); j/k move the selection the rail previews.
        onSelect={() => {
          onSelect()
          onOpen()
        }}
        onOpen={onOpen}
      />
      {selected ? (
        <span className="sr-only">
          Keyboard actions: <Kbd keys="a" /> take, <Kbd keys="n" /> next move, <Kbd keys="s" /> snooze.
        </span>
      ) : null}
    </div>
  )
}
